import type { HTMLAttributes } from 'react';
import { cx } from '../utils/cx.js';
import styles from './SignalIndicator.module.css';

export type SignalLevel = 'red' | 'amber' | 'green' | 'off';
export type SignalIndicatorSize = 'xs' | 'sm';

export type SignalIndicatorProps = Omit<HTMLAttributes<HTMLSpanElement>, 'aria-label'> & {
  /** Which lamp lights up. `off` = all three dimmed (unknown / archived). */
  level: SignalLevel;
  /** Visible label rendered next to the lights. */
  label: string;
  /** Defaults to `label`. Set explicitly when needs more context for assistive tech. */
  ariaLabel?: string;
  size?: SignalIndicatorSize;
};

/**
 * Traffic-light inspired status indicator. Three dots [red, amber, green]
 * with the matching one lit, plus a status label. Reads severity at a
 * glance without depending on color alone (label + tooltip on hover).
 */
export function SignalIndicator({
  level,
  label,
  ariaLabel,
  size = 'sm',
  className,
  ...rest
}: SignalIndicatorProps) {
  const sizeClass = size === 'xs' ? styles['size-xs'] : '';
  return (
    <span
      role="status"
      aria-label={ariaLabel ?? label}
      title={ariaLabel ?? label}
      className={cx(styles.wrap, sizeClass, className)}
      {...rest}
    >
      <span className={styles.lights} aria-hidden>
        <span className={cx(styles.dot, styles.red, level === 'red' && styles.on)} />
        <span className={cx(styles.dot, styles.amber, level === 'amber' && styles.on)} />
        <span className={cx(styles.dot, styles.green, level === 'green' && styles.on)} />
      </span>
      <span className={cx(styles.label, styles[level])}>{label}</span>
    </span>
  );
}
